import { EmptyState } from "../EmptyState";

import type { ElementSnippet } from "../Chat/chatTypes";
import type { StepData } from "../Workflow";
import { type BrowserLinkItem, BrowserChrome } from "../BrowserChrome";
import { useNavigation } from "./useNavigation";
import { Page } from "./Page";
import { useMemo } from "react";
import useInspectMode from "./useInspectMode";
import useAgentControl from "./useAgentControl";
import useAgentControlBridge from "./useAgentControlBridge";
import { useBranchServerReady } from "../../hooks/useBranchServerReady";

type Props = {
  step: StepData;
  spaceSlug: string;
  spaceId: string;
  branchId: string;
  contentKey?: string;
  navBranchId?: string;
  displayUrl: string;
  openUrl?: string;
  browserLinks?: ReadonlyArray<BrowserLinkItem>;
  inspectMode?: boolean;
  elementSnippets?: readonly ElementSnippet[];
  onInspectModeChange?: (mode: boolean) => void;
  onElementSelect?: (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => void;
  onElementDeselect?: (index: number) => void;
};

export function LiveBrowser({
  step,
  spaceSlug,
  spaceId,
  branchId,
  contentKey,
  navBranchId,
  displayUrl,
  openUrl,
  browserLinks,
  inspectMode,
  onInspectModeChange,
  onElementSelect,
  onElementDeselect,
  elementSnippets,
}: Props) {
  const inspect = useInspectMode({
    enabled: inspectMode,
    stepColor: step.color,
    onElementSelect,
    onElementDeselect,
    elementSnippets,
  });
  const baseRoute = step.edgeRoute ?? "";
  const navigation = useNavigation(baseRoute, navBranchId);
  const agentControl = useAgentControl();
  const branchServerReady = useBranchServerReady(branchId);
  const features = useMemo(
    () => [inspect.feature, navigation.feature, agentControl.feature],
    [inspect.feature, navigation.feature, agentControl.feature]
  );

  const previewHost = displayUrl.split("/")[0] ?? "";
  const navDisplayUrl = previewHost + navigation.path;

  useAgentControlBridge(agentControl, step.name, !!step.edgeRoute);

  return (
    <BrowserChrome
      displayUrl={navDisplayUrl}
      openUrl={openUrl}
      links={browserLinks}
      inspectMode={inspectMode}
      canBack={navigation.canBack}
      canForward={navigation.canForward}
      onBack={navigation.back}
      onForward={navigation.forward}
      onReload={navigation.reload}
      onInspectModeChange={onInspectModeChange}
    >
      {step.edgeRoute && branchServerReady ? (
        // Serve via the display branch so the live (main) branch carries no
        // `?branch=` param — otherwise the page reports its own branched URL
        // back into the bar, diverging from the clean copy/open URL.
        <Page
          reloadKey={`${contentKey ?? ""}:${navigation.reloadToken}`}
          spaceId={spaceId}
          spaceSlug={spaceSlug}
          edgeRoute={step.edgeRoute}
          branchId={navBranchId ?? null}
          externalId={step.routeExternalId}
          features={features}
        />
      ) : step.edgeRoute ? (
        <EmptyState
          iconName="earth"
          iconSize={24}
          maxWidth={220}
          title="Loading page…"
        />
      ) : (
        <EmptyState
          iconName="earth"
          iconSize={24}
          maxWidth={220}
          title="No page yet"
          description="This step doesn’t serve a webpage on this branch."
        />
      )}
    </BrowserChrome>
  );
}
